import axios from 'axios';

import {
  endSession,
  getValidAccessToken,
  refreshTokenOnce
} from './auth-token';
import { buildAuthHeaders } from './auth-headers';
import { ENTITY_SCOPE_HEADER, refusedForKnownUnreachableSelection } from './entity-scope-header';

// Guard against duplicate registration — setInterceptor() is called in
// multiple places (CallBack, App); without this flag each call stacks
// another interceptor onto axios.interceptors.
let interceptorsRegistered = false;

export const setInterceptor = () => {
  if (interceptorsRegistered) return;
  interceptorsRegistered = true;

  // REQUEST interceptor — proactively refresh token if expiring within the
  // shared refresh window (see auth-token.ts). User never sees a 401/403
  // from normal token expiry.
  axios.interceptors.request.use(
    async (config) => {
      let token: string | null = null;
      try {
        token = await getValidAccessToken();
      } catch {
        // Refresh attempt failed (refresh token expired / revoked). End the
        // session cleanly; let the request go through unauthenticated so
        // axios resolves and the navigation can take over.
        endSession();
      }

      // BC-7, matching api-base.ts. Every axios URL this interceptor sees is
      // built by getAPIUrls(), so there is no third-party origin to exclude;
      // the two calls that reach Keycloak's own endpoints use axios.create(),
      // which does not inherit these interceptors.
      //
      // The delete matters: the 401/403 retry below re-sends the SAME config
      // through this interceptor, so a header set on the first attempt would
      // otherwise survive a retry that should carry none (DV-3421).
      const auth = buildAuthHeaders(token);
      if (auth.Authorization) {
        config.headers.Authorization = auth.Authorization;
      }
      if (auth[ENTITY_SCOPE_HEADER]) {
        config.headers[ENTITY_SCOPE_HEADER] = auth[ENTITY_SCOPE_HEADER];
      } else {
        delete config.headers[ENTITY_SCOPE_HEADER];
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // RESPONSE interceptor — safety net for server-side token invalidation
  // (Keycloak session revoked, realm restart) which bypass the expiry check.
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // DV-3421: no refresh for a refusal of a selection already known to be
      // out of reach — see refusedForKnownUnreachableSelection.
      if (
        status === 403 &&
        refusedForKnownUnreachableSelection(originalRequest?.headers?.[ENTITY_SCOPE_HEADER])
      ) {
        return Promise.reject(error);
      }

      // _retry flag prevents infinite loops
      if ((status === 401 || status === 403) && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshTokenOnce();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          endSession();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
